import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { apiErr, formatNum } from "../lib/utils";
import { Button } from "./ui/button";
import { Input, Label } from "./ui/input";
import { Badge } from "./ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "./ui/table";
import { Plus, Trash2, Pencil, Check, X, Building2, Radar, MapPin, MapPinOff, Sparkles, Layers, ChevronDown, ChevronRight, FolderTree, MoveRight } from "lucide-react";
import { toast } from "sonner";
import { Facility360 } from "./Facility360";
import { FacilityLocationPicker } from "./FacilityLocationPicker";
import { Combobox } from "./ui/combobox";

const MEASURE_KEYS = ["area", "energy", "volume", "mass", "weight"];
const BLANK_FORM = { name: "", location: "", address: "", postal_code: "", country: "", currency: "", units: {}, lat: "", lng: "", group_id: "", open_date: "", close_date: "" };
const LEASED_OPTS = [
  { v: "inherit", l: "Use tenant default" },
  { v: "review", l: "Flag for review" },
  { v: "scope2", l: "Operational control → Scope 2" },
  { v: "scope3_cat8", l: "Not operated → Scope 3 Cat 8" },
];
const LEASED_SHORT = { review: "Review", scope2: "→ Scope 2", scope3_cat8: "→ Scope 3.8" };
const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

export function FacilitiesManager({ canEdit }) {
  const [data, setData] = useState(null);
  const [metrics, setMetrics] = useState({});
  const [groups, setGroups] = useState({ groups: [], flat: [] });
  const [form, setForm] = useState(BLANK_FORM);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", location: "", country: "", lat: "", lng: "" });
  const [view360, setView360] = useState(null);
  const [countries, setCountries] = useState([]);
  const [dimensions, setDimensions] = useState({});
  const [addingCountry, setAddingCountry] = useState(false);
  const [newCountry, setNewCountry] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [sel, setSel] = useState({});
  const [bulkGroup, setBulkGroup] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const load = () => {
    api.get("/facilities").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/facilities/dashboard").then((r) => {
      const m = {};
      (r.data.facilities || []).forEach((f) => { m[f.name] = f; });
      setMetrics(m);
    }).catch(() => {});
    api.get("/facility-groups").then((r) => setGroups(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/locale/countries").then((r) => setCountries(r.data.countries || [])).catch(() => {});
    api.get("/locale/dimensions").then((r) => setDimensions(r.data.measures || {})).catch(() => {});
  }, []);

  const currencyOptions = useMemo(() => {
    const s = new Set();
    countries.forEach((c) => c.currency && s.add(c.currency));
    ["SGD", "MYR", "IDR", "USD", "EUR", "GBP", "VND", "PHP", "THB"].forEach((x) => s.add(x));
    return [...s].sort();
  }, [countries]);

  const countryOptions = useMemo(() => ([
    ...countries.map((c) => ({ value: c.country, label: c.country })),
    { value: "__add__", label: "＋ Country not listed…" },
  ]), [countries]);

  const groupOptions = useMemo(() => ([
    { value: "", label: "Ungrouped" },
    ...(groups.flat || []).map((g) => ({ value: g.id, label: g.label || g.name })),
  ]), [groups]);
  const groupLabel = (gid) => (groups.flat || []).find((g) => g.id === gid)?.label || (gid ? "" : "Ungrouped");

  const pickCountry = (country) => {
    if (country === "__add__") { setAddingCountry(true); return; }
    const c = countries.find((x) => x.country === country);
    setForm((f) => ({
      ...f, country,
      location: f.location && f.location !== f.country ? f.location : country,
      currency: c?.currency || f.currency,
      units: c?.units ? { ...c.units } : f.units,
    }));
  };

  const suggestCountry = async () => {
    const country = newCountry.trim();
    if (!country) return;
    setSuggesting(true);
    try {
      const { data: r } = await api.post("/locale/suggest", { country });
      setCountries((prev) => (prev.some((x) => x.country === country)
        ? prev : [...prev, { country, currency: r.currency, units: r.units }].sort((a, b) => a.country.localeCompare(b.country))));
      setForm((f) => ({
        ...f, country,
        location: f.location && f.location !== f.country ? f.location : country,
        currency: r.currency || f.currency,
        units: r.units ? { ...r.units } : f.units,
      }));
      setAddingCountry(false); setNewCountry("");
      if (r.status === "pending") {
        toast.success(`AI-proposed locale for ${country} — using it for this facility. Queued for admin review to add to the master.`);
      } else {
        toast.success(`${country} is already in the locale master`);
      }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSuggesting(false); }
  };

  const onPinChange = async ({ lat, lng }) => {
    setForm((f) => ({ ...f, lat: String(lat), lng: String(lng) }));
    const hadCountry = !!form.country;
    try {
      const { data: g } = await api.get("/facilities/reverse-geocode", { params: { lat, lng } });
      if (!g.country) return;
      if (!hadCountry) { pickCountry(g.country); toast.success(`Country set to ${g.country} from the map pin`); }
      else if (form.country !== g.country) {
        toast.message(`Pin looks like ${g.country}`, { description: "Update the country field if this site is there." });
      }
    } catch { /* offline / no match — leave country as-is */ }
  };

  const create = async () => {
    try {
      const body = {
        name: form.name, location: form.location || null,
        address: form.address || null, postal_code: form.postal_code || null,
        country: form.country || undefined,
        currency: form.currency || undefined,
        units: Object.keys(form.units || {}).length ? form.units : undefined,
        group_id: form.group_id || "",
        open_date: form.open_date || "", close_date: form.close_date || "",
      };
      if (form.lat !== "" && form.lng !== "") { body.lat = +form.lat; body.lng = +form.lng; }
      const { data: r } = await api.post("/facilities", body);
      toast.success(r.facility?.location_key ? "Facility added — placed on the hazard map" : "Facility added");
      setForm(BLANK_FORM); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const save = async (f) => {
    if (!editForm.country) { toast.error("Country is required — select this facility's country to save"); return; }
    try {
      const body = { name: editForm.name, location: editForm.location || null, country: editForm.country,
                     address: editForm.address || null, postal_code: editForm.postal_code || null,
                     group_id: editForm.group_id || "", open_date: editForm.open_date || "", close_date: editForm.close_date || "" };
      if (editForm.lat !== "" && editForm.lng !== "") { body.lat = +editForm.lat; body.lng = +editForm.lng; }
      body.leased_boundary = editForm.leased_boundary === "inherit" ? "" : (editForm.leased_boundary || "");
      const r = await api.patch(`/facilities/${f.id}`, body);
      toast.success(r.data.records_updated ? `Renamed — ${r.data.records_updated} record(s) updated` : "Facility updated");
      setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Remove '${f.name}' from the master list? Records keep their current facility label.`)) return;
    try { await api.delete(`/facilities/${f.id}`); toast.success("Facility removed"); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  const selIds = useMemo(() => Object.keys(sel).filter((k) => sel[k]), [sel]);
  const bulkMove = async () => {
    try {
      const r = await api.post("/facilities/bulk-move", { facility_ids: selIds, group_id: bulkGroup || null });
      toast.success(`${r.data.moved} facility(ies) moved to ${bulkGroup ? groupLabel(bulkGroup) : "Ungrouped"}`);
      setSel({}); setBulkGroup(""); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const rollup = (facs) => facs.reduce((a, f) => ({
    records: a.records + (data.usage[f.name] || 0),
    emissions_t: a.emissions_t + (metrics[f.name]?.emissions_t || 0),
    energy_mwh: a.energy_mwh + (metrics[f.name]?.energy_mwh || 0),
  }), { records: 0, emissions_t: 0, energy_mwh: 0 });

  if (!data) return <div className="p-6 text-muted-foreground font-mono text-sm">Loading facilities…</div>;

  const colCount = canEdit ? 9 : 8;
  const facByGroup = {};
  data.facilities.forEach((f) => { (facByGroup[f.group_id || "__ungrouped__"] ||= []).push(f); });
  const topGroups = groups.groups || [];

  const toggleSel = (id) => setSel((p) => ({ ...p, [id]: !p[id] }));

  const renderRow = (f, indent) => {
    const isEd = editing === f.id;
    return (
      <TableRow key={f.id} data-testid="facility-row">
        {canEdit && (
          <TableCell className="w-8">
            <button type="button" onClick={() => toggleSel(f.id)} data-testid={`facility-select-${f.id}`}
              className={`h-4 w-4 border rounded-sm grid place-items-center ${sel[f.id] ? "bg-accent border-accent" : "border-border"}`}>
              {sel[f.id] && <Check className="h-2.5 w-2.5 text-accent-foreground" />}
            </button>
          </TableCell>
        )}
        <TableCell className="font-medium text-sm" style={indent ? { paddingLeft: 28 } : undefined}>
          {isEd ? <Input value={editForm.name} onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} className="h-7 w-48 text-xs" data-testid="facility-edit-name" /> : (
            <span className="inline-flex items-center gap-1.5">
              {f.name}
              {f.close_date && <Badge variant="outline" className="text-[8px] text-red-400 border-red-400/40" data-testid={`facility-closed-${f.id}`}>Closed · {f.close_date}</Badge>}
            </span>
          )}
          {!isEd && f.open_date && <div className="text-[10px] text-muted-foreground" data-testid={`facility-open-${f.id}`}>Opened {f.open_date}</div>}
        </TableCell>
        <TableCell className="text-xs" data-testid={`facility-country-${f.id}`}>
          {isEd ? (
            <Combobox
              value={editForm.country || ""}
              onChange={(v) => setEditForm((p) => ({ ...p, country: v === "__add__" ? "" : v }))}
              options={countryOptions.filter((o) => o.value !== "__add__")}
              placeholder="Select country"
              searchPlaceholder="Search countries…"
              className="h-7 w-40 text-xs"
              triggerTestId={`facility-edit-country-${f.id}`}
            />
          ) : f.country ? (
            <Badge variant="outline" className="text-[10px]">{f.country}{f.currency ? ` · ${f.currency}` : ""}</Badge>
          ) : (
            <Badge variant="warning" className="text-[9px] gap-0.5" data-testid={`facility-country-missing-${f.id}`} title="This facility has no country — set it on next edit">
              <X className="h-2.5 w-2.5" /> Country required
            </Badge>
          )}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">
          {isEd ? (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <Input value={editForm.location} onChange={(e) => setEditForm((p) => ({ ...p, location: e.target.value }))} placeholder="location" className="h-7 w-28 text-xs" data-testid="facility-edit-location" />
                <Input type="number" step="0.0001" placeholder="lat" value={editForm.lat} onChange={(e) => setEditForm((p) => ({ ...p, lat: e.target.value }))} className="h-7 w-20 text-xs" data-testid="facility-edit-lat" />
                <Input type="number" step="0.0001" placeholder="lng" value={editForm.lng} onChange={(e) => setEditForm((p) => ({ ...p, lng: e.target.value }))} className="h-7 w-20 text-xs" data-testid="facility-edit-lng" />
              </div>
              <div className="flex items-center gap-1">
                <Input value={editForm.address} onChange={(e) => setEditForm((p) => ({ ...p, address: e.target.value }))} placeholder="street address (for bill matching)" className="h-7 w-52 text-xs" data-testid="facility-edit-address" />
                <Input value={editForm.postal_code} onChange={(e) => setEditForm((p) => ({ ...p, postal_code: e.target.value }))} placeholder="postal" className="h-7 w-20 text-xs" data-testid="facility-edit-postal" />
              </div>
              <div className="flex items-center gap-1">
                <Select value={editForm.group_id || "__none__"} onValueChange={(v) => setEditForm((p) => ({ ...p, group_id: v === "__none__" ? "" : v }))}>
                  <SelectTrigger className="h-7 w-44 text-xs" data-testid="facility-edit-group"><SelectValue placeholder="Group" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="__none__">Ungrouped</SelectItem>
                    {(groups.flat || []).map((g) => <SelectItem key={g.id} value={g.id}>{g.label || g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" value={editForm.open_date || ""} onChange={(e) => setEditForm((p) => ({ ...p, open_date: e.target.value }))} className="h-7 w-32 text-xs" data-testid="facility-edit-open" title="Open date" />
                <Input type="date" value={editForm.close_date || ""} onChange={(e) => setEditForm((p) => ({ ...p, close_date: e.target.value }))} className="h-7 w-32 text-xs" data-testid="facility-edit-close" title="Close date" />
              </div>
            </div>
          ) : (
            <span>
              {f.location || "—"}
              {(f.address || f.postal_code) && (
                <span className="block text-[10px] text-muted-foreground" data-testid={`facility-address-${f.id}`}>{[f.address, f.postal_code].filter(Boolean).join(" · ")}</span>
              )}
              {f.lat != null && (
                <span className="text-[10px] text-muted-foreground font-mono ml-1.5" data-testid={`facility-coords-${f.id}`}>
                  {Number(f.lat).toFixed(2)}, {Number(f.lng).toFixed(2)}
                  <a href={`https://www.google.com/maps?q=${f.lat},${f.lng}`} target="_blank" rel="noopener noreferrer" title="Open in Google Maps (new tab)"
                    className="ml-1 underline decoration-dotted hover:text-accent" data-testid={`facility-map-link-${f.id}`}>map</a>
                </span>
              )}
              {f.coordinates_missing && (
                <Badge variant="warning" className="text-[9px] gap-0.5 ml-1.5" data-testid={`facility-coords-missing-${f.id}`} title="No coordinates on file — add them on edit to place this site on the hazard map">
                  <X className="h-2.5 w-2.5" /> No coordinates
                </Badge>
              )}
            </span>
          )}
        </TableCell>
        <TableCell className="text-xs" data-testid={`facility-leased-${f.id}`}>
          {isEd ? (
            <Select value={editForm.leased_boundary || "inherit"} onValueChange={(v) => setEditForm((p) => ({ ...p, leased_boundary: v }))}>
              <SelectTrigger className="h-7 w-44 text-xs" data-testid={`facility-edit-leased-${f.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{LEASED_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
          ) : f.leased_boundary ? (
            <Badge variant="outline" className="text-[9px]" title="Per-facility leased-asset boundary override for imports">{LEASED_SHORT[f.leased_boundary] || f.leased_boundary}</Badge>
          ) : (
            <span className="text-[10px] text-muted-foreground">Default</span>
          )}
        </TableCell>
        <TableCell className="text-right font-mono text-xs" data-testid="facility-emissions-cell">{metrics[f.name] ? metrics[f.name].emissions_t.toLocaleString() : "0"}</TableCell>
        <TableCell className="text-right font-mono text-xs" data-testid="facility-energy-cell">{metrics[f.name] ? metrics[f.name].energy_mwh.toLocaleString() : "0"}</TableCell>
        <TableCell className="text-right"><Badge variant={data.usage[f.name] ? "lime" : "outline"} className="text-[10px]" data-testid="facility-usage-badge">{data.usage[f.name] || 0}</Badge></TableCell>
        <TableCell>
          <div className="flex items-center gap-0.5 justify-end">
            <Button variant="ghost" size="sm" className="h-7 text-[10px] gap-1" onClick={() => setView360(f)} data-testid={`facility-360-btn-${f.id}`}>
              <Radar className="h-3.5 w-3.5 text-accent" /> 360°
            </Button>
            {canEdit && (isEd ? (
              <>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => save(f)} data-testid="facility-save-btn"><Check className="h-3.5 w-3.5 text-[#00E65B]" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(null)}><X className="h-3.5 w-3.5 text-muted-foreground" /></Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(f.id); setEditForm({ name: f.name, location: f.location || "", address: f.address || "", postal_code: f.postal_code || "", country: f.country || "", lat: f.lat ?? "", lng: f.lng ?? "", leased_boundary: f.leased_boundary || "inherit", group_id: f.group_id || "", open_date: f.open_date || "", close_date: f.close_date || "" }); }} data-testid="facility-edit-btn"><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(f)} data-testid="facility-delete-btn"><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
              </>
            ))}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const GroupHeaderRow = ({ node, members, sub }) => {
    const r = rollup(members);
    const open = !collapsed[node.id];
    return (
      <TableRow className="bg-surface/60" data-testid={`facility-group-header-${slug(node.name)}`}>
        <TableCell colSpan={colCount} className="py-2">
          <div className={`flex items-center gap-2 ${sub ? "pl-6" : ""}`}>
            <button type="button" onClick={() => setCollapsed((c) => ({ ...c, [node.id]: !c[node.id] }))} className="text-muted-foreground hover:text-foreground" data-testid={`facility-group-toggle-${slug(node.name)}`}>
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {sub ? <FolderTree className="h-3.5 w-3.5 text-accent/70" /> : <Layers className="h-4 w-4 text-accent" />}
            <span className={`font-display font-semibold ${sub ? "text-xs" : "text-sm"}`}>{node.name}</span>
            <div className="flex-1" />
            <div className="flex items-center gap-3 text-[11px] font-mono text-muted-foreground">
              <span data-testid={`facility-group-count-${slug(node.name)}`}><Building2 className="h-3 w-3 inline mr-0.5" />{members.length}</span>
              <span>{formatNum(r.records)} rec</span>
              <span className="text-[#00E65B]">{formatNum(r.emissions_t, { decimals: 1 })} tCO₂e</span>
              <span className="text-[#60A5FA]">{formatNum(r.energy_mwh, { decimals: 1 })} MWh</span>
            </div>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="card-flat" data-testid="facilities-manager">
      <div className="p-4 border-b border-border bg-surface flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Building2 className="h-4 w-4 text-accent" /> Facility master list</h2>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-xl">
            Canonical site names for your organisation, organised by group. Assign them to ESG records in Data Management,
            and imports auto-match facility columns against this list. Add a street address + postal code so
            uploaded utility bills auto-match to the right site by location — before any alias is learned.
            {data.unassigned > 0 && <span className="text-[#FFB800]"> {data.unassigned} record(s) currently unassigned.</span>}
          </p>
        </div>
        {(() => {
          const total = data.facilities.length;
          const missing = data.facilities.filter((f) => f.coordinates_missing).length;
          return missing > 0 ? (
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-[#FFB800] bg-[#FFB800]/10 border border-[#FFB800]/40 rounded-sm px-2.5 py-1.5 shrink-0" data-testid="facilities-coords-coverage" title="Sites without map coordinates won't appear on the hazard map — add them on edit. Blank coordinates are never defaulted.">
              <MapPinOff className="h-3.5 w-3.5 shrink-0" /> {missing} of {total} {total === 1 ? "facility" : "facilities"} missing coordinates
            </div>
          ) : total > 0 ? (
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-lime-400 bg-lime-400/10 border border-lime-400/30 rounded-sm px-2.5 py-1.5 shrink-0" data-testid="facilities-coords-coverage">
              <MapPin className="h-3.5 w-3.5 shrink-0" /> All {total} {total === 1 ? "facility has" : "facilities have"} coordinates
            </div>
          ) : null;
        })()}
      </div>

      {canEdit && (
        <div className="p-4 border-b border-border space-y-3" data-testid="add-facility-form">
          <div className="flex flex-wrap items-end gap-3">
            <div><Label>Facility name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Tuas Plant 1" className="w-56" data-testid="facility-name-input" /></div>
            <div><Label>Group</Label>
              <div className="flex items-center gap-1">
                <Select value={form.group_id || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, group_id: v === "__none__" ? "" : v }))}>
                  <SelectTrigger className="w-48" data-testid="facility-group-select"><SelectValue placeholder="Ungrouped" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="__none__">Ungrouped</SelectItem>
                    {(groups.flat || []).map((g) => <SelectItem key={g.id} value={g.id}>{g.label || g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5">New groups in Settings → Group Master</p>
            </div>
            <div><Label>Country <span className="text-red-400">*</span></Label>
              <Combobox
                value={form.country || ""}
                onChange={(v) => pickCountry(v)}
                options={countryOptions}
                placeholder="Select country"
                searchPlaceholder="Search countries…"
                emptyText="No country — use '＋ Country not listed…'"
                className="w-48"
                triggerTestId="facility-country-select"
                contentTestId="facility-country-options"
              />
              {!form.country && <p className="text-[10px] text-[#FFB800] mt-0.5" data-testid="facility-country-required">Country is required</p>}
            </div>
            <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="Pre-filled from country" className="w-44" data-testid="facility-location-input" /></div>
            <div><Label>Open date</Label><Input type="date" value={form.open_date} onChange={(e) => setForm((f) => ({ ...f, open_date: e.target.value }))} className="w-36" data-testid="facility-open-input" /></div>
            <div><Label>Close date</Label><Input type="date" value={form.close_date} onChange={(e) => setForm((f) => ({ ...f, close_date: e.target.value }))} className="w-36" data-testid="facility-close-input" /></div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div><Label>Street address</Label><Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} placeholder="e.g. 429 Woodlands St 41 #02-256" className="w-64" data-testid="facility-address-input" /></div>
            <div><Label>Postal code</Label><Input value={form.postal_code} onChange={(e) => setForm((f) => ({ ...f, postal_code: e.target.value }))} placeholder="e.g. 730429" className="w-32" data-testid="facility-postal-input" /></div>
            <div><Label>Currency</Label>
              <Select value={form.currency || "none"} onValueChange={(v) => setForm((f) => ({ ...f, currency: v === "none" ? "" : v }))}>
                <SelectTrigger className="w-32" data-testid="facility-currency-select"><SelectValue placeholder="Currency" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="none">—</SelectItem>
                  {currencyOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="primary" size="sm" disabled={!form.name.trim() || !form.country} onClick={create} data-testid="add-facility-btn"><Plus className="h-3.5 w-3.5" /> Add facility</Button>
          </div>
          {addingCountry && (
            <div className="flex flex-wrap items-end gap-2 p-2.5 rounded-md bg-accent/5 border border-accent/20" data-testid="country-suggest-row">
              <div><Label className="text-[11px] flex items-center gap-1"><Sparkles className="h-3 w-3 text-accent" /> Country not in the master?</Label>
                <Input value={newCountry} onChange={(e) => setNewCountry(e.target.value)} placeholder="e.g. Cambodia" className="w-48" data-testid="new-country-input" /></div>
              <Button variant="outline" size="sm" disabled={!newCountry.trim() || suggesting} onClick={suggestCountry} data-testid="suggest-country-btn">
                <Sparkles className="h-3.5 w-3.5" /> {suggesting ? "Suggesting…" : "Suggest locale"}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { setAddingCountry(false); setNewCountry(""); }}>Cancel</Button>
              <p className="text-[10px] text-muted-foreground basis-full">We'll propose its customary units + currency (AI) for this facility now, and queue it for a super-admin to add to the shared master.</p>
            </div>
          )}
          {form.country && (
            <div className="flex flex-wrap items-end gap-3" data-testid="facility-uom-row">
              <span className="label-eyebrow text-[10px] self-center">Units of measure</span>
              {MEASURE_KEYS.map((m) => (
                <div key={m}><Label className="capitalize text-[11px]">{m}</Label>
                  <Select value={(form.units || {})[m] || "none"} onValueChange={(v) => setForm((f) => ({ ...f, units: { ...f.units, [m]: v === "none" ? undefined : v } }))}>
                    <SelectTrigger className="w-24 h-8 text-xs" data-testid={`facility-uom-${m}`}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {((dimensions[m] || {}).units || []).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-2" data-testid="facility-coords-section">
            <div className="flex items-center gap-3">
              <span className="label-eyebrow text-[10px] flex items-center gap-1"><MapPin className="h-3 w-3 text-accent" /> Coordinates (optional)</span>
              <Input type="number" step="0.00001" placeholder="latitude" value={form.lat} onChange={(e) => setForm((f) => ({ ...f, lat: e.target.value }))} className="w-32 h-8 text-xs" data-testid="facility-lat-input" />
              <Input type="number" step="0.00001" placeholder="longitude" value={form.lng} onChange={(e) => setForm((f) => ({ ...f, lng: e.target.value }))} className="w-32 h-8 text-xs" data-testid="facility-lng-input" />
              <span className="text-[10px] text-muted-foreground">Click the map to drop a pin, or type coordinates — the country is auto-detected from the pin, and the site snaps to the nearest hazard location.</span>
            </div>
            <FacilityLocationPicker
              lat={form.lat === "" ? null : +form.lat}
              lng={form.lng === "" ? null : +form.lng}
              onChange={onPinChange}
            />
          </div>
          <p className="text-[10px] text-muted-foreground">Country pre-fills the location, currency and units of measure from the Country/Locale Master — all remain editable per facility.</p>
        </div>
      )}

      {canEdit && selIds.length > 0 && (
        <div className="p-3 border-b border-border bg-accent/5 flex flex-wrap items-center gap-3" data-testid="facility-bulk-bar">
          <span className="text-xs font-medium">{selIds.length} selected</span>
          <MoveRight className="h-3.5 w-3.5 text-muted-foreground" />
          <Select value={bulkGroup || "__none__"} onValueChange={(v) => setBulkGroup(v === "__none__" ? "" : v)}>
            <SelectTrigger className="w-52 h-8 text-xs" data-testid="facility-bulk-group-select"><SelectValue placeholder="Move to group…" /></SelectTrigger>
            <SelectContent className="max-h-64">
              <SelectItem value="__none__">Ungrouped</SelectItem>
              {(groups.flat || []).map((g) => <SelectItem key={g.id} value={g.id}>{g.label || g.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="primary" size="sm" onClick={bulkMove} data-testid="facility-bulk-move-btn"><MoveRight className="h-3.5 w-3.5" /> Move to group</Button>
          <Button variant="ghost" size="sm" onClick={() => setSel({})} data-testid="facility-bulk-clear">Clear</Button>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            {canEdit && <TableHead className="w-8" />}
            <TableHead>Name</TableHead><TableHead>Country</TableHead><TableHead>Location</TableHead><TableHead>Leased boundary</TableHead><TableHead className="text-right">Emissions (tCO₂e)</TableHead><TableHead className="text-right">Energy (MWh)</TableHead><TableHead className="text-right">Records</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.facilities.length === 0 && (
            <TableRow><TableCell colSpan={colCount} className="text-center text-muted-foreground py-8 text-sm">No facilities yet — add your sites above so records can be assigned.</TableCell></TableRow>
          )}
          {topGroups.map((node) => {
            const direct = facByGroup[node.id] || [];
            const childMembers = (node.children || []).flatMap((c) => facByGroup[c.id] || []);
            const members = [...direct, ...childMembers];
            const open = !collapsed[node.id];
            return (
              <React.Fragment key={node.id}>
                <GroupHeaderRow node={node} members={members} />
                {open && direct.map((f) => renderRow(f, false))}
                {open && (node.children || []).map((c) => {
                  const cm = facByGroup[c.id] || [];
                  const csopen = !collapsed[c.id];
                  return (
                    <React.Fragment key={c.id}>
                      <GroupHeaderRow node={c} members={cm} sub />
                      {csopen && cm.map((f) => renderRow(f, true))}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            );
          })}
          {(facByGroup.__ungrouped__ || []).length > 0 && (
            <React.Fragment>
              <GroupHeaderRow node={{ id: "__ungrouped__", name: "Ungrouped" }} members={facByGroup.__ungrouped__} />
              {!collapsed.__ungrouped__ && facByGroup.__ungrouped__.map((f) => renderRow(f, false))}
            </React.Fragment>
          )}
        </TableBody>
      </Table>
      {view360 && <Facility360 facility={view360} canEdit={canEdit} onClose={() => setView360(null)} onSaved={load} />}
    </div>
  );
}
